import type { FC, ReactNode } from "react";

import styled from "styled-components";

import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { PixelPlaceholderLoader, PlainTooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { InfoTooltip } from "app/components/React/Tooltip/InfoTooltip/InfoTooltip";
import { translate } from "app/filters/i18nFilter";
import { minVisitsAbbrFilter } from "app/filters/ngFilters";
import { CountryFiilterWithIconAndNameOnly } from "app/pages/market-research/components/StartPageCountryFilter";
import { KeywordGroupEditorHelpers } from "app/pages/keyword-analysis/KeywordGroupEditorHelpers";
import type { ICountryObject } from "app/services/CountryService";
import { Subtitle as StyledSubtitle } from "pro-features/styled components/StyledBoxSubtitle/src/StyledBoxSubtitle";

import { useKeywordListComposerContext } from "../KeywordListComposerContext";
import { trackPopupClick } from "../tracking";
import type { Keyword } from "../types";

interface SubtitleProps {
    keywords: Keyword[];
    subtitle?: ReactNode;
}

const { carbon } = colorsPalettes;

const BoldSubtitle = styled.span`
    ${setFontVariant("subtitle1", carbon[500])};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Value = styled.span`
    background-color: ${carbon[500]};
    ${setFontVariant("h5", carbon[0])};
    padding: 0 6px;
    border-radius: 6px;
`;

export const Left: FC<SubtitleProps> = ({ keywords, subtitle }) => {
    const volume = keywords.reduce((acc, { volume }) => {
        if (!volume) {
            return acc;
        }
        return acc + volume;
    }, 0);
    const formattedVolume = minVisitsAbbrFilter()(volume);
    return (
        <Flex
            gap="8px"
            alignItems="center"
        >
            <BoldSubtitle>{subtitle}</BoldSubtitle>
            <StyledSubtitle>{translate("auto.keyword.list.generator.header.subtitle.yearly.volume")}</StyledSubtitle>
            <Value>{formattedVolume}</Value>
            <InfoTooltip infoText={translate("auto.keyword.list.generator.header.subtitle.yearly.volume.tooltip")} />
        </Flex>
    );
};

const RightContainer = styled.div`
    display: flex;
    gap: 8px;
    flex: 1 1 auto;
    justify-content: end;
    align-items: center;
`;

export const Right = () => {
    const maxKeywordsCount = KeywordGroupEditorHelpers.getMaxGroupCount() as number;
    const { keywords, country, changeCountry } = useKeywordListComposerContext();
    const keywordsCount = keywords.length;
    const maxCountString =
        maxKeywordsCount < 10000
            ? maxKeywordsCount
            : translate("KeywordAnalysis.keywordgroup.wizard.header.counter.unlimited");

    const handleSetCountry = (country: ICountryObject) => {
        changeCountry(country);
        trackPopupClick({ event: "Country Filter", countryName: country.text });
    };

    return (
        <RightContainer>
            <PlainTooltip tooltipContent={translate("auto.keyword.list.generator.header.subtitle.country.tooltip")}>
                <div>
                    <CountryFiilterWithIconAndNameOnly
                        country={country}
                        setCountry={handleSetCountry}
                    />
                </div>
            </PlainTooltip>
            <span>{`${keywordsCount} / ${maxCountString} ${translate("common.keywords")}`}</span>
        </RightContainer>
    );
};

const WithLoadingHOC = (Component: FC) =>
    function Render() {
        const { suggestionsStatus } = useKeywordListComposerContext();
        if (suggestionsStatus === "pending") {
            return (
                <PixelPlaceholderLoader
                    width={"100%"}
                    height={"28px"}
                />
            );
        }
        return <Component />;
    };

const SubtitleInner = () => {
    const { keywords } = useKeywordListComposerContext();
    const count = keywords.length;

    return (
        <Flex>
            <Left
                keywords={keywords}
                subtitle={translate("auto.keyword.list.generator.header.subtitle.amout", { count })}
            />
            <Right />
        </Flex>
    );
};
export const Subtitle = WithLoadingHOC(SubtitleInner);

const PreviewSubtitleView: FC<{ count: number }> = ({ count }) => {
    return (
        <span>
            {count} {translate("common.keywords")} {translate("common.generated")}
        </span>
    );
};

const PreviewSubtitleInner = () => {
    const { keywords } = useKeywordListComposerContext();
    const count = keywords.length;
    return (
        <Flex>
            <Left
                keywords={keywords}
                subtitle={<PreviewSubtitleView count={count} />}
            />
        </Flex>
    );
};

export const PreviewSubtitle = WithLoadingHOC(PreviewSubtitleInner);
